import { API } from 'lib/api';
import type {
  IAccountingCode,
  IAccountingCodeResponse,
} from 'types/AccountingCode/AccountingCodes.types';
import { ONBOARDING_ENDPOINTS } from 'constant/endpoints/onboarding';
import { RESOURCE_ENDPOINTS } from 'constant/endpoints/resources';
import {
  EMPLOYEE_ENDPOINTS,
  EMPLOYEE_TYPE_ENDPOINTS,
} from 'constant/endpoints/employee';
import { HTTP_HEADERS } from 'constant/headers';
import type {
  AddTrackingCategoryRequest,
  BulkArchiveAccountingCodesParams,
  BulkUpdateOnboardingResourcesParams,
  BulkUpdateOnboardingResourcesResponse,
  CreateOnboardingEmploymentTypeParams,
  OnboardingAccountingCodesQueryParams,
  OnboardingCreateResourcePayload,
  OnboardingCreateResourceResponse,
  OnboardingPoAutogenRuleResponse,
  OnboardingEmployeeListParams,
  OnboardingEmployeeListResponse,
  OnboardingEmployeePatchParams,
  OnboardingEmployeeRateSheet,
  OnboardingEmployeeResourceCode,
  OnboardingEmploymentType,
  ResourceTemplatesResponse,
  ResourceTypeDropdownItem,
  TenantOnboardingPatchParams,
  TenantOnboardingResponse,
  TenantOnboardingSkippedPatchParams,
  TenantOnboardingSkippedResponse,
  XeroTrackingCategoriesResponse,
} from '../types/onboardingApi';

export const fetchOnboardingPoAutogenRule =
  async (): Promise<OnboardingPoAutogenRuleResponse> => {
    const { data } = await API.get<OnboardingPoAutogenRuleResponse>(
      ONBOARDING_ENDPOINTS.GET_PO_AUTOGEN_RULE,
    );
    return data;
  };

export const fetchOnboardingAccountingCodes = async (
  params: OnboardingAccountingCodesQueryParams,
): Promise<IAccountingCodeResponse> => {
  const { data } = await API.get<IAccountingCodeResponse>(
    ONBOARDING_ENDPOINTS.GET_ACCOUNTING_CODES,
    {
      params,
    },
  );
  return data;
};

const ONBOARDING_ACCOUNTING_CODES_FETCH_CHUNK = 500;

/** Loads every page for the given filters so “select all” can span pagination. */
export const fetchAllOnboardingAccountingCodesForSelection = async (
  params: Pick<
    OnboardingAccountingCodesQueryParams,
    'q' | 'order_by' | 'order_type'
  >,
): Promise<IAccountingCode[]> => {
  const accumulated: IAccountingCode[] = [];
  let page = 1;
  let totalPages = 1;
  do {
    const res = await fetchOnboardingAccountingCodes({
      page,
      per_page: ONBOARDING_ACCOUNTING_CODES_FETCH_CHUNK,
      q: params.q,
      order_by: params.order_by,
      order_type: params.order_type,
    });
    accumulated.push(...(res.data ?? []));
    totalPages = res.total_pages ?? 1;
    page += 1;
  } while (page <= totalPages);
  return accumulated;
};

export const fetchOnboardingAccountingCodesDropdown = async (): Promise<
  IAccountingCode[]
> => {
  const { data } = await API.get(ONBOARDING_ENDPOINTS.GET_ACCOUNTING_CODES, {
    params: { page: 1, per_page: 1000, q: '' },
  });
  return (data.data ?? []) as IAccountingCode[];
};

export const fetchOnboardingResourceTemplates =
  async (): Promise<ResourceTemplatesResponse> => {
    const { data } = await API.get<ResourceTemplatesResponse>(
      ONBOARDING_ENDPOINTS.GET_RESOURCE_CODE_LIST,
      { params: { page: 1, per_page: 25 } },
    );
    return data;
  };

export const fetchOnboardingResourceTypeDropdown = async (): Promise<
  ResourceTypeDropdownItem[]
> => {
  const { data } = await API.get<{ data: ResourceTypeDropdownItem[] }>(
    RESOURCE_ENDPOINTS.RESOURCE_TYPE_DROPDOWN,
    { params: { page: 1, per_page: 1000 } },
  );
  return data?.data ?? [];
};

export const bulkArchiveAccountingCodes = async (
  params: BulkArchiveAccountingCodesParams,
): Promise<{ message: string }> => {
  const { data } = await API.delete(
    ONBOARDING_ENDPOINTS.BULK_ARCHIVE_ACCOUNTING_CODES,
    {
      data: params,
    },
  );
  return data;
};

/** Bulk onboarding resource accounting update. Uses POST until backend specifies otherwise. */
export const bulkUpdateOnboardingResources = async (
  params: BulkUpdateOnboardingResourcesParams,
): Promise<BulkUpdateOnboardingResourcesResponse> => {
  const { data } = await API.put<BulkUpdateOnboardingResourcesResponse>(
    ONBOARDING_ENDPOINTS.RESOURCES_BULK_ACCOUNTING_UPDATE,
    params,
  );
  return data;
};

export const fetchOnboardingXeroTrackingCategories = async (
  tenantId: string,
): Promise<XeroTrackingCategoriesResponse> => {
  const { data } = await API.get<XeroTrackingCategoriesResponse>(
    ONBOARDING_ENDPOINTS.XERO_TRACKING_CATEGORY,
    { headers: { [HTTP_HEADERS.TENANT_ID]: tenantId } },
  );
  return data;
};

export const addTrackingCategory = async ({
  tenantId,
  ...params
}: AddTrackingCategoryRequest): Promise<{ message: string }> => {
  const { data } = await API.post(
    ONBOARDING_ENDPOINTS.XERO_TRACKING_CATEGORY,
    params,
    tenantId ? { headers: { [HTTP_HEADERS.TENANT_ID]: tenantId } } : undefined,
  );
  return data;
};

export const completeOnboarding = async (): Promise<{ message: string }> => {
  const { data } = await API.put(ONBOARDING_ENDPOINTS.COMPLETE_ONBOARDING);
  return data;
};

export const patchOnboardingEmployee = async ({
  id,
  ...fields
}: OnboardingEmployeePatchParams): Promise<void> => {
  await API.patch(`${EMPLOYEE_ENDPOINTS.EDIT_EMPLOYEE(id)}`, fields);
};

export const createOnboardingEmploymentType = async (
  params: CreateOnboardingEmploymentTypeParams,
): Promise<OnboardingEmploymentType> => {
  const { data } = await API.post<OnboardingEmploymentType>(
    EMPLOYEE_TYPE_ENDPOINTS.CREATE_EMPLOYMENT_TYPE,
    params,
  );
  return data;
};

export const fetchOnboardingEmployeeResourceCodes = async (): Promise<
  OnboardingEmployeeResourceCode[]
> => {
  const { data } = await API.get<OnboardingEmployeeResourceCode[]>(
    EMPLOYEE_ENDPOINTS.EMPLOYEE_RESOURCE_CODES,
  );
  return Array.isArray(data) ? data : [];
};

export const fetchOnboardingEmployeeList = async (
  params: OnboardingEmployeeListParams,
): Promise<OnboardingEmployeeListResponse> => {
  const { data } = await API.get<OnboardingEmployeeListResponse>(
    EMPLOYEE_ENDPOINTS.LIST_EMPLOYEES,
    { params },
  );
  return data;
};

export const fetchOnboardingEmployeeRateSheet =
  async (): Promise<OnboardingEmployeeRateSheet> => {
    const { data } = await API.get<OnboardingEmployeeRateSheet>(
      EMPLOYEE_ENDPOINTS.GET_RESOURCE_CODES,
    );
    return data;
  };

export const fetchTenantOnboarding =
  async (): Promise<TenantOnboardingResponse> => {
    const { data } = await API.get<TenantOnboardingResponse>(
      ONBOARDING_ENDPOINTS.TENANT_ONBOARDING,
    );
    return data;
  };

export const patchTenantOnboarding = async (
  params: TenantOnboardingPatchParams,
): Promise<TenantOnboardingResponse> => {
  const { data } = await API.patch<TenantOnboardingResponse>(
    ONBOARDING_ENDPOINTS.TENANT_ONBOARDING,
    params,
  );
  return data;
};

export const fetchTenantOnboardingSkipped =
  async (): Promise<TenantOnboardingSkippedResponse> => {
    const { data } = await API.get<TenantOnboardingSkippedResponse>(
      ONBOARDING_ENDPOINTS.TENANT_ONBOARDING_SKIPPED,
    );
    return data;
  };

export const patchTenantOnboardingSkipped = async (
  params: TenantOnboardingSkippedPatchParams,
): Promise<TenantOnboardingSkippedResponse> => {
  const { data } = await API.patch<TenantOnboardingSkippedResponse>(
    ONBOARDING_ENDPOINTS.TENANT_ONBOARDING_SKIPPED,
    params,
  );
  return data;
};

export const createOnboardingResource = async (
  params: OnboardingCreateResourcePayload,
): Promise<OnboardingCreateResourceResponse> => {
  const { data } = await API.post<{ data: OnboardingCreateResourceResponse }>(
    RESOURCE_ENDPOINTS.CREATE_RESOURCE,
    { ...params, other_properties: {} },
  );
  return data.data;
};
